import { useFinanceReport } from '@/hooks/useFinanceReport';
import { IncomeSummaryCards } from './finance/IncomeSummaryCards';
import { PaymentMethodsPanel } from './finance/PaymentMethodsPanel';
import { CreditControlTable } from './finance/CreditControlTable';
import { InvoiceStatusCards } from './finance/InvoiceStatusCards';
import { FinancialSummaryTable } from './finance/FinancialSummaryTable';
import { BankReconciliationPanel } from './finance/BankReconciliationPanel';
import { ExpensesReport } from './ExpensesReport';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';

interface FinanceReportProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
  compareEnabled?: boolean;
}

export const FinanceReport = ({ startDate, endDate, timezone, compareEnabled = false }: FinanceReportProps) => {
  const {
    incomeSummary,
    paymentMethods,
    outstandingJobs,
    agingBuckets,
    invoiceStats,
    overallMetrics,
    expenseCategorySummary,
    totalExpenses,
    totalOutstanding,
    isLoading,
    isInvoiceStatsLoading,
  } = useFinanceReport(startDate, endDate, timezone);

  // Comparison data (deferred: only when enabled and main data loaded)
  const { data: comparisonData, isLoading: comparisonLoading } = useComparisonMetrics(
    startDate,
    endDate,
    timezone,
    !isLoading && !!incomeSummary && compareEnabled
  );

  return (
    <Tabs defaultValue="overview" className="space-y-6">
      <TabsList>
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>
        <TabsTrigger value="credit-control">Credit Control</TabsTrigger>
        <TabsTrigger value="expenses">Expenses</TabsTrigger>
        <TabsTrigger value="summary">P&L Summary</TabsTrigger>
      </TabsList>

      {/* Overview Tab */}
      <TabsContent value="overview" className="space-y-8">
        {/* Income Summary */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Income Summary</h2>
          <IncomeSummaryCards
            data={incomeSummary}
            isLoading={isLoading}
            comparisonData={compareEnabled ? comparisonData : undefined}
            comparisonLoading={compareEnabled && comparisonLoading}
          />
        </section>

        {/* Payment Methods Breakdown */}
        <section>
          <PaymentMethodsPanel
            data={paymentMethods}
            isLoading={isLoading}
            startDate={startDate}
            endDate={endDate}
            timezone={timezone}
          />
        </section>

        <Separator />

        {/* Invoice Status */}
        <section>
          <InvoiceStatusCards
            data={invoiceStats}
            isLoading={isInvoiceStatsLoading}
          />
        </section>
      </TabsContent>

      {/* Reconciliation Tab */}
      <TabsContent value="reconciliation" className="space-y-6">
        <BankReconciliationPanel
          startDate={startDate}
          endDate={endDate}
          timezone={timezone}
          systemTotals={paymentMethods}
          isLoadingSystem={isLoading}
        />
      </TabsContent>

      {/* Credit Control Tab */}
      <TabsContent value="credit-control" className="space-y-6">
        <CreditControlTable
          jobs={outstandingJobs}
          agingBuckets={agingBuckets}
          totalOutstanding={totalOutstanding}
          isLoading={isLoading}
        />
      </TabsContent>

      {/* Expenses Tab */}
      <TabsContent value="expenses" className="space-y-6">
        <ExpensesReport />
      </TabsContent>

      {/* P&L Summary Tab */}
      <TabsContent value="summary" className="space-y-6">
        <FinancialSummaryTable
          metrics={overallMetrics}
          expenseSummary={expenseCategorySummary}
          totalExpenses={totalExpenses}
          isLoading={isLoading}
        />
      </TabsContent>
    </Tabs>
  );
};
